import InfoIcon from '@mui/icons-material/Info';
import { DateRange } from '@mui/lab';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControlLabel,
    Divider,
} from '@mui/material';
import DateFromToFilter from 'components/shared/date-from-to-filter/date-from-to-filter.component';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReports } from '../../context/reports-context';
import { ReportDescriptionDialog } from './report-description-dialog';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { ExportButton } from 'components/shared/export-button/export-button.component';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
];

export const ReportsActions: React.FC = () => {
    const { t: translate, i18n } = useTranslation(['reports', 'common', 'orders', 'users']);

    const {
        reports,
        selectedReport,
        setSelectedReport,
        reportData,
        clinics,
        categories,
        getReportDescription,
        descriptionData,
        descriptionLoading,
        createReport,
    } = useReports();

    const [selectedReportId, setSelectedReportId] = useState('');
    const [showSpinner, setShowSpinner] = useState(false);
    const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [reportFilters, setReportFilters] = useState({ clinic: 'ALL_CLINICS', completedOrders: false });

    const [dateFromTo, setDateFromTo] = useState<DateRange<Date>>(() => {
        const today = new Date();
        const from = new Date();
        from.setMonth(today.getMonth() - 4);
        return [from, today];
    });

    const STATUS_OPTIONS: Record<string, string> = {
        SEND_BY_CUSTOMER: 'SEND BY CUSTOMER',
        RECEIVED_BY_LAB: 'RECEIVED BY LAB',
        SENT_BY_LAB: 'SENT BY LAB',
        APPROVED: 'APPROVED',
    };

    const flattenColumns = (columns: any[]) =>
        columns.flatMap((column) =>
            Array.isArray(column.value)
                ? column.value.map((item: any) => ({
                      ...column,
                      name: item.name,
                      value: item.value,
                      originalColumn: column.name,
                  }))
                : column,
        );

    const resolveCellValue = (row: any, column: any) => {
        if (column.originalColumn) {
            const item = row[column.originalColumn]?.find((i: any) => i.name === column.name);
            return item?.count ?? item?.categoryTotalAmount ?? 0;
        }

        const raw = row[column.name];
        const value =
            raw && typeof raw === 'object'
                ? raw.stringValue ?? raw.intValue ?? `${raw.firstName || ''} ${raw.lastName || ''}`.trim()
                : raw;

        if (value == null || value === '' || value === 'null') {
            if (['ADHERENCE', 'PATIENT_NEEDS', 'LANGUAGE'].includes(column.name)) return '0';
            if (column.name === 'adherencePercentage') return '0%';
            return '-';
        }

        const str = String(value);

        return typeof value === 'number' || /\d/.test(str) || /\s/.test(str)
            ? str
            : translate(str, { ns: ['reports', 'orders'], defaultValue: str });
    };

    const formatExportData = (data: any[], columns: any[]) =>
        data.map((row) => {
            const flat: any = {};
            columns.forEach((col) => {
                flat[translate(`reports:${col.value}`)] = resolveCellValue(row, col);
            });
            return flat;
        });

    const handleCreateReport = async () => {
        if (!selectedReport?._id) return;

        const clinicIds = reportFilters.clinic === 'ALL_CLINICS' ? clinics.map((c) => c._id) : [reportFilters.clinic];

        const payload = {
            id: selectedReport._id,
            options: {
                ...(dateFromTo[0] && dateFromTo[1] && { dateFromTo }),
                clinics: clinicIds,
                ...(selectedStatuses.length && { status: selectedStatuses.map((s) => s.toLowerCase()) }),
                ...(selectedCategories.length && { category: selectedCategories }),
                ...(reportFilters.completedOrders &&
                    ['NUMBER_OF_ORDERS', 'ECONOMY'].includes(selectedReport.name) && {
                        completedOrders: true,
                    }),
                ...(selectedReport.name === 'ECONOMY_DONE_ORDERS_PER_DENTIST' && { approvedOrders: true }),
            },
        };

        setShowSpinner(true);
        await createReport(payload);
        setShowSpinner(false);
    };

    const isCreateDisabled = selectedReport?.filters?.date === true && (!dateFromTo[0] || !dateFromTo[1]);

    const flatColumns = reportData ? flattenColumns(reportData.columns) : [];

    return (
        <Box id="reports">
            <Paper elevation={2} sx={{ p: 3 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>{translate('reports:CHOOSE_REPORT')}</InputLabel>
                            <Select
                                value={selectedReportId}
                                onChange={(e) => {
                                    setSelectedReportId(e.target.value);
                                    setSelectedReport(reports.find((r) => r._id === e.target.value) || null);
                                }}
                                input={<OutlinedInput label={translate('reports:CHOOSE_REPORT')} />}
                            >
                                {reports.map((r) => (
                                    <MenuItem key={r._id} value={r._id}>
                                        {translate(`reports:${r.name}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {selectedReport?.filters?.date && (
                        <Grid item xs={12} md={8}>
                            <DateFromToFilter
                                dateFromTo={dateFromTo}
                                dateFilterType="Date Filter"
                                onDateChange={(d) => d[0] && d[1] && setDateFromTo(d)}
                            />
                        </Grid>
                    )}
                </Grid>

                <Grid container spacing={1} sx={{ mt: 2 }}>
                    {selectedReport?.filters?.clinic && (
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>{translate('users:SELECT_CLINIC')}</InputLabel>
                                <Select
                                    value={reportFilters.clinic}
                                    onChange={(e) => setReportFilters({ ...reportFilters, clinic: e.target.value })}
                                    input={<OutlinedInput label={translate('users:SELECT_CLINIC')} />}
                                >
                                    <MenuItem value="ALL_CLINICS">{translate('orders:ALL_CLINICS')}</MenuItem>
                                    {clinics.map((c) => (
                                        <MenuItem key={c._id} value={c._id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {selectedReport?.filters?.category && categories.length > 0 && (
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>{translate('reports:SELECT_CATEGORY')}</InputLabel>
                                <Select
                                    multiple
                                    value={selectedCategories}
                                    onChange={(e) => setSelectedCategories(e.target.value as string[])}
                                    input={<OutlinedInput label={translate('reports:SELECT_CATEGORY')} />}
                                    renderValue={() =>
                                        selectedCategories.length
                                            ? selectedCategories
                                                  .map((id) => categories.find((c) => c._id === id)?.name)
                                                  .join(', ')
                                            : translate('reports:SELECT_CATEGORY')
                                    }
                                >
                                    {categories.map((c) => (
                                        <MenuItem key={c._id} value={c._id}>
                                            <Checkbox checked={selectedCategories.includes(c._id)} />
                                            <ListItemText primary={`${c.name} (${c.actionSet.title})`} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {selectedReport?.filters?.status && (
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>{translate('reports:SELECT_STATUS')}</InputLabel>
                                <Select
                                    multiple
                                    value={selectedStatuses}
                                    onChange={(e) => setSelectedStatuses(e.target.value as string[])}
                                    input={<OutlinedInput label={translate('reports:SELECT_STATUS')} />}
                                    renderValue={() =>
                                        selectedStatuses.length
                                            ? selectedStatuses.map((s) => STATUS_OPTIONS[s]).join(', ')
                                            : translate('reports:SELECT_STATUS')
                                    }
                                >
                                    {Object.entries(STATUS_OPTIONS).map(([key, value]) => (
                                        <MenuItem key={key} value={key}>
                                            <Checkbox checked={selectedStatuses.includes(key)} />
                                            <ListItemText primary={value} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {['NUMBER_OF_ORDERS', 'ECONOMY'].includes(selectedReport?.name || '') && (
                        <Grid item xs={12} md={4}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={reportFilters.completedOrders}
                                        onChange={(e) =>
                                            setReportFilters({
                                                ...reportFilters,
                                                completedOrders: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label={translate('reports:ONLY_GET_COMPLETED_ORDER')}
                            />
                        </Grid>
                    )}
                </Grid>

                {selectedReport && (
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                            onClick={() => {
                                getReportDescription({
                                    language: i18n.language,
                                    reportName: selectedReport.name,
                                });
                                setShowDescriptionDialog(true);
                            }}
                        >
                            <span>{translate('reports:REPORT_DESCRIPTION')}</span>
                            <InfoIcon fontSize="small" />
                        </Box>

                        <Button variant="contained" onClick={handleCreateReport} disabled={isCreateDisabled}>
                            {translate('common:CREATE')}
                        </Button>
                    </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Results */}
                <Box sx={{ mt: 4 }}>
                    {showSpinner && <SkeletonTable columns={skeletonColumns} rows={3} />}

                    {!showSpinner && !reportData && (
                        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            {translate('reports:CREATE_REPORT_HELP_TEXT')}
                        </Box>
                    )}

                    {!showSpinner && reportData && reportData.data.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            {translate('reports:NO_REPORT_DATA')}
                        </Box>
                    )}

                    {!showSpinner && reportData && reportData.data.length > 0 && (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <ExportButton
                                    data={formatExportData(reportData.data, flatColumns)}
                                    filename={translate('reports:' + selectedReport?.name)}
                                    format="both"
                                    variant="outlined"
                                />
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {flatColumns.map((c, i) => (
                                                <TableCell key={i}>
                                                    {/\s/.test(c.value) ? c.value : translate(`reports:${c.value}`)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.data.map((row: any, rowIndex: number) => (
                                            <TableRow key={row.id || `row-${rowIndex}`}>
                                                {flatColumns.map((c: any, i: number) => (
                                                    <TableCell key={`${row.id || rowIndex}-${i}`}>
                                                        {resolveCellValue(row, c)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>
            </Paper>

            <ReportDescriptionDialog
                showDescriptionDialog={showDescriptionDialog}
                setShowDescriptionDialog={setShowDescriptionDialog}
                descriptionLoading={descriptionLoading}
                descriptionData={descriptionData}
            />
        </Box>
    );
};
